<main class="app-shell" [class.is-reading]="focusedNodeId()" [class.is-clean-overview]="isCleanOverview()" [class.is-globe-overview]="isGlobeOverview()" *ngIf="data() as mapData; else loading">
  <aside class="sidebar" [attr.aria-label]="text('mapActions')">
    <header class="brand">
      <div class="brand-row">
        <img src="awakenmap-icon.png" alt="" width="42" height="42">
        <div>
          <p class="kicker">{{ text('brandKicker') }}</p>
          <h1>AwakenMap</h1>
        </div>
      </div>
      <strong class="brand-theme">{{ text('brandTheme') }}</strong>
      <p class="intro-copy">
        {{ text('intro') }}
      </p>
    </header>

    <label class="search-box">
      <span>{{ text('search') }}</span>
      <input
        type="search"
        [placeholder]="text('searchPlaceholder')"
        [ngModel]="query()"
        (ngModelChange)="query.set($event)"
      >
    </label>

    <section>
      <div class="section-title">{{ text('themeLayers') }}</div>
      <div class="filter-list">
        <label class="filter-item" *ngFor="let category of mapData.categories">
          <input
            type="checkbox"
            [checked]="enabledCategories().has(category.id)"
            (change)="toggleCategory(category.id, $any($event.target).checked)"
          >
          <span>{{ category.name }}</span>
          <i class="swatch" [style.background]="category.color"></i>
        </label>
      </div>
    </section>

    <section>
      <div class="section-title">{{ text('guidedPaths') }}</div>
      <div class="trail-list">
        <button class="trail-button" type="button" *ngFor="let trail of mapData.trails" (click)="startTrail(trail)">
          {{ trail.title }}
        </button>
      </div>
    </section>

    <section class="stats" aria-live="polite">
      <div>
        <span>{{ visibleNodes().length }}</span>
            <small>{{ text('visiblePoints') }}</small>
      </div>
      <div>
        <span>{{ renderedEdges().length }}</span>
            <small>{{ text('links') }}</small>
      </div>
    </section>
  </aside>

  <section class="map-stage" [attr.aria-label]="text('navigableMap')">
    <div class="topbar">
      <div>
        <strong>{{ text('navigableMap') }}</strong>
        <span>{{ activeContext() }}</span>
      </div>
      <div class="mode-row" [attr.aria-label]="text('viewMode')">
        <button type="button" [title]="text('explore')" [attr.aria-label]="text('explore')" [class.is-active]="mapMode() === 'explore'" (click)="setMode('explore')">{{ text('explore') }}</button>
        <button type="button" [title]="text('total')" [attr.aria-label]="text('total')" [class.is-active]="mapMode() === 'total'" (click)="setMode('total')">{{ text('total') }}</button>
        <button *ngIf="mapMode() === 'total'" type="button" [title]="text('deepMode')" [attr.aria-label]="text('deepMode')" [class.is-active]="deepMode()" (click)="toggleDeepMode()">{{ text('deep') }}</button>
      </div>
      <div class="tool-row">
        <button type="button" [title]="text('zoomOut')" [attr.aria-label]="text('zoomOut')" (click)="zoom(1.16)">-</button>
        <button type="button" [title]="text('zoomIn')" [attr.aria-label]="text('zoomIn')" (click)="zoom(0.86)">+</button>
        <button type="button" [title]="text('centerMap')" [attr.aria-label]="text('centerMap')" (click)="resetView()">[]</button>
      </div>
    </div>

    <div class="overview-actions" *ngIf="isGlobeOverview()" [attr.aria-label]="text('mapActions')">
      <div class="language-toggle" [attr.aria-label]="text('language')">
        <button type="button" [class.is-active]="language() === 'pt'" (click)="setLanguage('pt')">PT</button>
        <button type="button" [class.is-active]="language() === 'en'" (click)="setLanguage('en')">EN</button>
      </div>
      <button type="button" [attr.aria-label]="text('search')" [title]="text('search')" (click)="toggleSearch()">{{ text('search') }}</button>
      <button type="button" [attr.aria-label]="text('filters')" [title]="text('filters')" (click)="toggleFilterPanel()">{{ text('filters') }}</button>
      <button type="button" *ngIf="mapMode() === 'explore'" [attr.aria-label]="text('total')" [title]="text('total')" (click)="setMode('total')">{{ text('total') }}</button>
      <button type="button" *ngIf="mapMode() === 'total'" [attr.aria-label]="text('explore')" [title]="text('explore')" (click)="setMode('explore')">{{ text('explore') }}</button>
      <button type="button" *ngIf="mapMode() === 'total'" [attr.aria-label]="text('deepMode')" [title]="text('deepMode')" [class.is-active]="deepMode()" (click)="toggleDeepMode()">{{ text('deep') }}</button>
    </div>

    <div class="overview-popover search-popover" *ngIf="isGlobeOverview() && searchOpen()">
      <label>
        <span>{{ text('globeSearch') }}</span>
        <input
          #globeSearchInput
          type="search"
          [placeholder]="text('globeSearchPlaceholder')"
          [ngModel]="globeSearch()"
          (ngModelChange)="globeSearch.set($event)"
          (keyup.enter)="searchGlobe()"
        >
      </label>
      <button type="button" (click)="searchGlobe()">{{ text('locate') }}</button>
    </div>

    <div class="overview-popover filter-popover" *ngIf="isGlobeOverview() && filterOpen()">
      <div class="popover-head">
        <strong>{{ text('layerFilters') }}</strong>
        <button type="button" [attr.aria-label]="text('closeFilters')" (click)="toggleFilterPanel()">x</button>
      </div>
      <label *ngFor="let layer of globeLayers" [class]="'globe-layer-check ' + globeLayerClass(layer)">
        <input
          type="checkbox"
          [checked]="isGlobeLayerEnabled(layer)"
          (change)="toggleGlobeLayer(layer, $any($event.target).checked)"
        >
        <span class="layer-dot"></span>
        <span>
          <strong>{{ globeLayerLabel(layer) }}</strong>
          <small>{{ globeLayerDescription(layer) }}</small>
        </span>
      </label>
    </div>

    <div class="globe-hints" *ngIf="isGlobeOverview()">
      <button type="button" [class.is-active]="autoRotate()" (click)="toggleAutoRotate()">
        <span>G</span><strong>{{ autoRotate() ? text('spinning') : text('stopped') }}</strong>
      </button>
      <button type="button" (click)="threeZoom('reset')">
        <span>R</span><strong>{{ text('recenter') }}</strong>
      </button>
    </div>

    <div class="discovered-card" *ngIf="isGlobeOverview()">
      <span>{{ mapMode() === 'total' ? text('atlasTotal') : text('discoveredNodes') }}</span>
      <strong>{{ mapMode() === 'total' ? globeNodeCount() : discoveredNodeCount() }} <small>/ {{ totalThemeCount() }}</small></strong>
      <em>{{ mapMode() === 'total' ? text('expandedView') : text('clickToDiscover') }}</em>
    </div>

    <div class="globe-zoom" *ngIf="isGlobeOverview()" [attr.aria-label]="text('zoomIn')">
      <button type="button" [attr.aria-label]="text('zoomIn')" (click)="threeZoom('in')">+</button>
      <button type="button" [attr.aria-label]="text('centerMap')" (click)="threeZoom('reset')">o</button>
      <button type="button" [attr.aria-label]="text('zoomOut')" (click)="threeZoom('out')">-</button>
    </div>

    <p class="overview-tagline" *ngIf="isGlobeOverview()">{{ mapMode() === 'total' ? text('totalTagline') : text('exploreTagline') }}</p>

    <aside class="globe-node-card" *ngIf="isGlobeOverview() && selectedGlobeNode() as node">
      <button type="button" class="card-close" [attr.aria-label]="text('close')" (click)="clearGlobeSelection()">x</button>
      <p [class]="'node-layer-label ' + globeLayerClass(nodeVisualLayer(node))">{{ globeLayerLabel(nodeVisualLayer(node)) }}</p>
      <h2>{{ node.title }}</h2>
      <p>{{ node.summary }}</p>
      <div class="globe-card-links" *ngIf="selectedGlobeConnections().length">
        <span>{{ text('connectedTo') }}</span>
        <button type="button" *ngFor="let related of selectedGlobeConnections()" (click)="selectNode(related.id)">
          {{ related.title }}
        </button>
      </div>
      <div class="globe-card-actions">
        <button type="button" class="details-button" (click)="focusedNodeId.set(node.id); focusNode(node)">
          {{ text('readTheory') }}
        </button>
        <a class="details-button is-secondary" [href]="topicUrl(node)">
          {{ text('fullStudy') }}
        </a>
      </div>
    </aside>
    <div
      #globeHost
      class="three-globe"
      [class.is-active]="isGlobeOverview()"
      aria-label="Globo terrestre 3D do AwakenMap"
      (pointermove)="threeGlobePointerMove($event)"
      (pointerleave)="clearGlobeHover()"
      (click)="threeGlobeClick($event)"
    ></div>

    <div class="layer-row" *ngIf="!isGlobeOverview()" [attr.aria-label]="text('viewMode')">
      <button
        type="button"
        *ngFor="let layer of layerOptions()"
        [class.is-active]="activeLayer() === layer.id"
        (click)="setLayer(layer.id)"
      >
        {{ layer.label }}
      </button>
    </div>

    <svg
      *ngIf="!isGlobeOverview()"
      #canvas
      id="mapCanvas"
      role="img"
      [attr.aria-label]="text('mapConnections')"
      [attr.viewBox]="viewBox().x + ' ' + viewBox().y + ' ' + viewBox().width + ' ' + viewBox().height"
      (pointerdown)="pointerDown($event)"
      (pointermove)="pointerMove($event)"
      (pointerup)="pointerUp($event)"
      (wheel)="wheel($event)"
    >
      <defs>
        <pattern id="grid" width="80" height="80" patternUnits="userSpaceOnUse">
          <path d="M 80 0 L 0 0 0 80" fill="none" stroke="rgba(255,255,255,0.06)" stroke-width="1"></path>
        </pattern>
        <radialGradient
          *ngFor="let category of mapData.categories"
          [attr.id]="'node-gradient-' + category.id"
          cx="32%"
          cy="24%"
          r="76%"
        >
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.88"></stop>
          <stop offset="20%" [attr.stop-color]="category.color" stop-opacity="1"></stop>
          <stop offset="72%" [attr.stop-color]="category.color" stop-opacity="0.96"></stop>
          <stop offset="100%" stop-color="#08090b" stop-opacity="0.7"></stop>
        </radialGradient>
        <filter id="node-depth-shadow" x="-80%" y="-80%" width="260%" height="280%">
          <feDropShadow dx="0" dy="10" stdDeviation="8" flood-color="#000000" flood-opacity="0.58"></feDropShadow>
        </filter>
        <radialGradient id="earth-ocean" cx="34%" cy="26%" r="78%">
          <stop offset="0%" stop-color="#1d5d92" stop-opacity="0.56"></stop>
          <stop offset="34%" stop-color="#082b50" stop-opacity="0.92"></stop>
          <stop offset="76%" stop-color="#020916" stop-opacity="1"></stop>
          <stop offset="100%" stop-color="#00030a" stop-opacity="1"></stop>
        </radialGradient>
        <radialGradient id="earth-night" cx="36%" cy="24%" r="78%">
          <stop offset="0%" stop-color="#5fa4d8" stop-opacity="0.18"></stop>
          <stop offset="44%" stop-color="#06172b" stop-opacity="0.1"></stop>
          <stop offset="84%" stop-color="#00030a" stop-opacity="0.56"></stop>
          <stop offset="100%" stop-color="#000000" stop-opacity="0.72"></stop>
        </radialGradient>
        <clipPath id="earth-disc-clip">
          <circle cx="0" cy="0" r="758"></circle>
        </clipPath>
        <pattern
          id="earth-texture-pattern"
          patternUnits="userSpaceOnUse"
          x="-930"
          y="-523"
          width="1860"
          height="1046"
          [attr.patternTransform]="globeTextureTransform()"
        >
          <image href="earth-night-map.svg" x="0" y="0" width="1860" height="1046" preserveAspectRatio="xMidYMid slice"></image>
        </pattern>
        <radialGradient id="node-gradient-physical" cx="32%" cy="24%" r="76%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.9"></stop>
          <stop offset="24%" stop-color="#6dff72" stop-opacity="1"></stop>
          <stop offset="100%" stop-color="#053a16" stop-opacity="0.92"></stop>
        </radialGradient>
        <radialGradient id="node-gradient-conceptual" cx="32%" cy="24%" r="76%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.9"></stop>
          <stop offset="24%" stop-color="#5db8ff" stop-opacity="1"></stop>
          <stop offset="100%" stop-color="#07325f" stop-opacity="0.92"></stop>
        </radialGradient>
        <radialGradient id="node-gradient-cosmic" cx="32%" cy="24%" r="76%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.9"></stop>
          <stop offset="24%" stop-color="#bd6cff" stop-opacity="1"></stop>
          <stop offset="100%" stop-color="#3b0a5f" stop-opacity="0.92"></stop>
        </radialGradient>
        <radialGradient id="node-gradient-blue" cx="32%" cy="24%" r="76%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.96"></stop>
          <stop offset="28%" stop-color="#48a6ff" stop-opacity="1"></stop>
          <stop offset="100%" stop-color="#052f83" stop-opacity="0.95"></stop>
        </radialGradient>
        <radialGradient id="node-gradient-green" cx="32%" cy="24%" r="76%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.96"></stop>
          <stop offset="28%" stop-color="#67ff75" stop-opacity="1"></stop>
          <stop offset="100%" stop-color="#075a20" stop-opacity="0.95"></stop>
        </radialGradient>
        <radialGradient id="node-gradient-gold" cx="32%" cy="24%" r="76%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.98"></stop>
          <stop offset="30%" stop-color="#ffd45a" stop-opacity="1"></stop>
          <stop offset="100%" stop-color="#80520b" stop-opacity="0.95"></stop>
        </radialGradient>
        <radialGradient id="node-gradient-purple" cx="32%" cy="24%" r="76%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.96"></stop>
          <stop offset="28%" stop-color="#c66cff" stop-opacity="1"></stop>
          <stop offset="100%" stop-color="#47107b" stop-opacity="0.95"></stop>
        </radialGradient>
        <radialGradient id="node-gradient-red" cx="32%" cy="24%" r="76%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.96"></stop>
          <stop offset="30%" stop-color="#ff616e" stop-opacity="1"></stop>
          <stop offset="100%" stop-color="#7c1020" stop-opacity="0.95"></stop>
        </radialGradient>
        <radialGradient id="node-gradient-cyan" cx="32%" cy="24%" r="76%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.96"></stop>
          <stop offset="30%" stop-color="#61f7ff" stop-opacity="1"></stop>
          <stop offset="100%" stop-color="#08636b" stop-opacity="0.95"></stop>
        </radialGradient>
      </defs>
      <rect class="space-rect" x="-5000" y="-5000" width="10000" height="10000" [attr.fill]="isCleanOverview() ? '#02060d' : 'url(#grid)'"></rect>

      <g class="planet-backdrop" *ngIf="!focusedNodeId()">
        <circle class="planet-glow" cx="0" cy="0" r="920"></circle>
        <circle class="planet-atmosphere" cx="0" cy="0" r="800"></circle>
        <circle class="planet-disc" cx="0" cy="0" r="760" fill="url(#earth-ocean)"></circle>
        <circle class="earth-texture" cx="0" cy="0" r="758" fill="url(#earth-texture-pattern)"></circle>
        <circle class="planet-night" cx="0" cy="0" r="758" fill="url(#earth-night)"></circle>
        <g class="planet-land" [attr.transform]="globeSurfaceTransform()">
          <path class="land land-america-north" d="M-705 -358 C-625 -455 -472 -470 -346 -400 C-254 -350 -182 -338 -120 -262 C-216 -234 -238 -158 -324 -140 C-410 -122 -434 -46 -510 -34 C-594 -20 -694 -116 -736 -230 C-754 -280 -742 -324 -705 -358 Z"></path>
          <path class="land land-america-south" d="M-344 -84 C-250 -60 -190 24 -204 130 C-216 220 -150 286 -122 368 C-96 448 -166 570 -248 588 C-330 496 -384 386 -372 276 C-360 168 -446 76 -414 0 C-404 -42 -382 -72 -344 -84 Z"></path>
          <path class="land land-europe" d="M-64 -372 C50 -438 210 -424 314 -338 C252 -280 150 -294 94 -238 C44 -188 -58 -214 -104 -280 C-132 -320 -114 -354 -64 -372 Z"></path>
          <path class="land land-africa" d="M62 -178 C188 -202 306 -98 330 44 C356 196 262 344 144 438 C28 336 -40 220 -28 70 C-18 -36 -22 -132 62 -178 Z"></path>
          <path class="land land-asia" d="M258 -292 C442 -392 684 -286 758 -102 C660 -76 596 -10 610 92 C514 132 430 76 332 106 C238 134 206 42 246 -48 C286 -136 190 -208 258 -292 Z"></path>
          <path class="land land-australia" d="M482 312 C612 282 732 346 776 466 C670 536 520 496 482 312 Z"></path>
          <path class="land land-greenland" d="M-384 -648 C-282 -716 -126 -692 -70 -598 C-150 -548 -304 -544 -384 -648 Z"></path>
        </g>
        <g class="planet-cities" [attr.transform]="globeSurfaceTransform()">
          <circle cx="-540" cy="-238" r="5"></circle><circle cx="-482" cy="-210" r="3"></circle><circle cx="-398" cy="-144" r="4"></circle>
          <circle cx="-288" cy="342" r="6"></circle><circle cx="-246" cy="294" r="3"></circle><circle cx="-318" cy="210" r="3"></circle>
          <circle cx="16" cy="-294" r="4"></circle><circle cx="98" cy="-248" r="5"></circle><circle cx="168" cy="-210" r="4"></circle>
          <circle cx="194" cy="-42" r="6"></circle><circle cx="260" cy="18" r="4"></circle><circle cx="292" cy="112" r="3"></circle>
          <circle cx="430" cy="-126" r="5"></circle><circle cx="528" cy="-84" r="4"></circle><circle cx="604" cy="-18" r="3"></circle>
          <circle cx="572" cy="370" r="5"></circle>
        </g>
        <g class="planet-spin" [attr.transform]="globeSurfaceTransform()">
          <ellipse cx="0" cy="0" rx="760" ry="190"></ellipse>
          <ellipse cx="0" cy="0" rx="760" ry="370"></ellipse>
          <ellipse cx="0" cy="0" rx="760" ry="560"></ellipse>
          <ellipse cx="0" cy="0" rx="225" ry="760"></ellipse>
          <ellipse cx="0" cy="0" rx="450" ry="760"></ellipse>
          <path d="M-760 -250 C-460 -390 -180 -300 50 -410 C290 -526 548 -402 740 -260"></path>
          <path d="M-700 290 C-410 170 -90 260 150 120 C390 -20 610 90 745 240"></path>
        </g>
      </g>

      <g
        class="focus-scene"
        *ngIf="activeDetailNode() as node"
        [attr.transform]="'translate(' + node.position.x + ' ' + node.position.y + ')'"
        (click)="selectNode(node.id)"
      >
        <circle class="focus-accent-orb" r="38" [attr.fill]="'url(#node-gradient-' + nodeVisualLayer(node) + ')'"></circle>

        <foreignObject
          class="focus-content"
          [attr.x]="focusContentBox().x"
          [attr.y]="focusContentBox().y"
          [attr.width]="focusContentBox().width"
          [attr.height]="focusContentBox().height"
        >
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            class="focus-card"
            (pointerdown)="$event.stopPropagation()"
            (pointermove)="$event.stopPropagation()"
            (pointerup)="$event.stopPropagation()"
            (click)="$event.stopPropagation()"
            (wheel)="$event.stopPropagation()"
          >
            <button type="button" class="focus-close" [attr.aria-label]="text('backToMap')" (click)="exitFocus($event)">x</button>
            <p class="kicker">{{ categoryById(node.categoryId)?.name }}</p>
            <h2>{{ node.title }}</h2>
            <div class="focus-article">
              <section *ngFor="let section of focusArticleSections()">
                <h3>{{ section.title }}</h3>
                <p>{{ section.body }}</p>
              </section>
            </div>

            <div class="focus-related" *ngIf="focusSceneNodes().length">
              <h3>{{ text('keepExploring') }}</h3>
              <div>
                <button
                  type="button"
                  *ngFor="let related of focusSceneNodes()"
                  (click)="selectNode(related.id)"
                >
                  {{ related.title }}
                </button>
              </div>
            </div>

            <a class="focus-study-link" [href]="topicUrl(node)">
              {{ text('fullStudy') }}
            </a>
          </div>
        </foreignObject>
      </g>

      <g class="islands" *ngIf="categoryIslands().length">
        <g
          class="island"
          *ngFor="let island of categoryIslands(); trackBy: trackIsland"
        >
          <ellipse
            [attr.cx]="island.x"
            [attr.cy]="island.y"
            [attr.rx]="island.rx"
            [attr.ry]="island.ry"
            [attr.fill]="island.color"
          ></ellipse>
          <text
            [attr.x]="island.x - island.rx + 48"
            [attr.y]="island.y - island.ry + 72"
          >
            {{ island.name }}
          </text>
        </g>
      </g>

      <ng-container *ngFor="let edge of renderedEdges(); trackBy: trackEdge">
        <path [attr.class]="edgeClass(edge)" [attr.d]="edgePath(edge)" [attr.stroke-width]="edgeWidth(edge)" fill="none"></path>
        <text
          *ngIf="shouldShowEdgeLabels()"
          class="edge-label"
          [attr.x]="edgeLabelPosition(edge).x"
          [attr.y]="edgeLabelPosition(edge).y"
          text-anchor="middle"
        >
          {{ edge.label }}
        </text>
      </ng-container>

      <g
        class="node"
        *ngFor="let node of renderedNodes(); trackBy: trackNode"
        [class.is-selected]="node.id === selectedNodeId()"
        [class.is-focused]="node.id === focusedNodeId()"
        [attr.transform]="'translate(' + nodeRenderPosition(node).x + ' ' + nodeRenderPosition(node).y + ')'"
        [style.opacity]="nodeVisibility(node)"
        (pointerdown)="$event.stopPropagation()"
        (pointermove)="$event.stopPropagation()"
        (pointerup)="$event.stopPropagation()"
        (click)="$event.stopPropagation(); selectNode(node.id)"
      >
        <g class="node-body" [attr.transform]="'scale(' + nodeVisualScale(node) + ')'">
        <circle [attr.class]="'node-glow layer-' + nodeVisualLayer(node) + ' tone-' + nodeTone(node)" [attr.r]="nodeRadius(node) + 10"></circle>
        <circle class="node-orb" [attr.r]="nodeRadius(node)" [attr.fill]="'url(#node-gradient-' + nodeGradient(node) + ')'" filter="url(#node-depth-shadow)"></circle>
        <ellipse class="node-shine" [attr.cx]="-nodeRadius(node) * 0.24" [attr.cy]="-nodeRadius(node) * 0.3" [attr.rx]="nodeRadius(node) * 0.36" [attr.ry]="nodeRadius(node) * 0.2"></ellipse>
        <circle class="node-core" [attr.r]="nodeRadius(node) - 4"></circle>
        <g class="node-icon" [ngSwitch]="iconKind(node)">
          <g *ngSwitchCase="'sun'" class="icon-sun">
            <circle r="11"></circle>
            <path d="M0 -22 L0 -15 M0 15 L0 22 M-22 0 L-15 0 M15 0 L22 0 M-15 -15 L-10 -10 M15 -15 L10 -10 M-15 15 L-10 10 M15 15 L10 10"></path>
          </g>
          <g *ngSwitchCase="'reptile'">
            <path d="M-18 0 Q0 -15 18 0 Q0 15 -18 0 Z"></path>
            <path d="M0 -10 L0 10"></path>
            <circle r="3"></circle>
          </g>
          <g *ngSwitchCase="'chip'">
            <rect x="-13" y="-13" width="26" height="26" rx="4"></rect>
            <path d="M-22 -8 L-13 -8 M-22 0 L-13 0 M-22 8 L-13 8 M13 -8 L22 -8 M13 0 L22 0 M13 8 L22 8 M-8 -22 L-8 -13 M0 -22 L0 -13 M8 -22 L8 -13 M-8 13 L-8 22 M0 13 L0 22 M8 13 L8 22"></path>
          </g>
          <g *ngSwitchCase="'book'">
            <path d="M-19 -16 H-3 Q0 -16 0 -12 V16 Q-4 12 -19 14 Z"></path>
            <path d="M19 -16 H3 Q0 -16 0 -12 V16 Q4 12 19 14 Z M-13 -7 H-5 M6 -7 H14"></path>
          </g>
          <g *ngSwitchCase="'coin'">
            <circle r="17"></circle>
            <path d="M0 -11 V11 M-8 -5 Q0 -12 8 -5 M-8 5 Q0 12 8 5"></path>
          </g>
          <g *ngSwitchCase="'portal'" class="icon-pulse">
            <circle r="18"></circle>
            <path d="M0 -18 C18 -8 18 8 0 18 C-18 8 -18 -8 0 -18 Z M-12 0 H12"></path>
          </g>
          <g *ngSwitchCase="'planet'">
            <circle r="13"></circle>
            <path d="M-24 4 C-11 -8 12 -8 24 4 M-22 7 C-8 17 10 17 22 7"></path>
          </g>
          <g *ngSwitchCase="'people'">
            <circle cx="-8" cy="-8" r="6"></circle>
            <circle cx="9" cy="-9" r="5"></circle>
            <path d="M-20 15 Q-8 2 4 15 M0 15 Q10 5 20 15"></path>
          </g>
          <g *ngSwitchCase="'drop'">
            <path d="M0 -21 C12 -5 18 4 18 12 C18 22 -18 22 -18 12 C-18 4 -12 -5 0 -21 Z"></path>
          </g>
          <g *ngSwitchCase="'shield'">
            <path d="M0 -21 L18 -13 V1 C18 12 10 19 0 23 C-10 19 -18 12 -18 1 V-13 Z"></path>
            <path d="M0 -13 V14"></path>
          </g>
          <g *ngSwitchCase="'gear'">
            <circle r="8"></circle>
            <path d="M0 -23 V-15 M0 15 V23 M-23 0 H-15 M15 0 H23 M-16 -16 L-11 -11 M16 -16 L11 -11 M-16 16 L-11 11 M16 16 L11 11"></path>
          </g>
          <g *ngSwitchCase="'hourglass'">
            <path d="M-14 -20 H14 M-14 20 H14 M-10 -18 C-10 -6 10 -6 10 0 C10 6 -10 6 -10 18 M10 -18 C10 -6 -10 -6 -10 0 C-10 6 10 6 10 18"></path>
          </g>
          <g *ngSwitchCase="'wave'">
            <path d="M-23 2 C-14 -12 -6 -12 0 2 C7 16 15 16 23 2"></path>
            <path d="M-23 13 C-14 -1 -6 -1 0 13 C7 27 15 27 23 13" transform="translate(0 -12)"></path>
          </g>
          <g *ngSwitchCase="'pin'">
            <path d="M0 22 C-13 6 -17 -1 -17 -9 C-17 -19 17 -19 17 -9 C17 -1 13 6 0 22 Z"></path>
            <circle r="6"></circle>
          </g>
          <g *ngSwitchCase="'person'">
            <circle cy="-11" r="8"></circle>
            <path d="M-17 20 Q0 2 17 20"></path>
          </g>
          <g *ngSwitchCase="'being'" class="icon-float">
            <path d="M0 -20 Q15 -8 15 6 Q8 20 0 20 Q-8 20 -15 6 Q-15 -8 0 -20 Z"></path>
            <path d="M-6 -2 H-2 M2 -2 H6 M-5 8 Q0 12 5 8"></path>
          </g>
          <g *ngSwitchCase="'leaf'">
            <path d="M-20 16 C-12 -16 10 -22 22 -20 C22 -3 11 17 -13 19 Z"></path>
            <path d="M-13 13 C-3 5 7 -3 18 -16"></path>
          </g>
          <g *ngSwitchCase="'ship'" class="icon-float">
            <path d="M-22 4 L0 -10 L22 4 L8 8 L0 14 L-8 8 Z"></path>
            <path d="M-6 2 L6 2 M-12 10 L-19 17 M12 10 L19 17"></path>
          </g>
          <g *ngSwitchCase="'mountain'">
            <path d="M-23 16 L-5 -16 L5 1 L12 -10 L24 16 Z"></path>
            <path d="M-5 -16 L-1 -4 L5 1"></path>
          </g>
          <g *ngSwitchCase="'lotus'" class="icon-pulse">
            <path d="M0 -20 C10 -8 10 8 0 18 C-10 8 -10 -8 0 -20 Z"></path>
            <path d="M-4 13 C-20 7 -21 -8 -8 -14 M4 13 C20 7 21 -8 8 -14"></path>
          </g>
          <g *ngSwitchCase="'bolt'">
            <path d="M4 -22 L-11 3 L2 3 L-4 22 L14 -5 L1 -5 Z"></path>
          </g>
          <g *ngSwitchCase="'eye'">
            <path d="M-22 0 Q0 -17 22 0 Q0 17 -22 0 Z"></path>
            <circle r="7"></circle>
          </g>
          <g *ngSwitchCase="'pyramid'">
            <path d="M-20 15 L0 -18 L20 15 Z"></path>
            <path d="M0 -18 L4 15 M-20 15 L20 15"></path>
          </g>
          <g *ngSwitchCase="'spark'" class="icon-pulse">
            <path d="M0 -22 L5 -5 L22 0 L5 5 L0 22 L-5 5 L-22 0 L-5 -5 Z"></path>
          </g>
          <g *ngSwitchDefault>
            <circle r="10"></circle>
            <path d="M-16 0 L16 0 M0 -16 L0 16"></path>
          </g>
        </g>
        <text *ngIf="shouldShowNodeLabel(node)" x="0" [attr.y]="nodeRadius(node) + 22" text-anchor="middle">{{ node.title }}</text>
        </g>
      </g>
    </svg>
  </section>

  <aside class="detail-panel" [attr.aria-label]="text('detailSummary')" hidden *ngIf="activeDetailNode() as node">
    <button class="close-detail" type="button" [attr.aria-label]="text('close')" (click)="closeDetail()">x</button>
    <p class="kicker">{{ text('detailSummary') }}</p>
    <h2>{{ node.title }}</h2>
    <p class="original-name" *ngIf="node.titleOriginal && node.titleOriginal !== node.title">
      {{ text('originalName') }}: {{ node.titleOriginal }}
    </p>
    <p>{{ node.summary }}</p>

    <div class="detail-meta">
      <span class="pill">{{ categoryById(node.categoryId)?.name }}</span>
      <span class="pill">{{ node.type }}</span>
      <span class="pill">{{ text('importance') }} {{ node.importance }}/5</span>
    </div>

    <a class="learn-more" [href]="topicUrl(node)">{{ text('fullStudy') }}</a>

    <div class="section-title">{{ text('nearbyConnections') }}</div>
    <button class="expand-button" type="button" (click)="toggleExpandedConnections()">
      {{ expandedConnections() ? text('hideSecondary') : text('expandSecondary') }}
    </button>
    <div class="related-list">
      <button
        class="related-item"
        type="button"
        *ngFor="let edge of relatedEdges()"
        (click)="relatedNode(edge) && selectNode(relatedNode(edge)!.id)"
      >
        {{ edge.label }}: {{ relatedNode(edge)?.title }}
      </button>
    </div>
  </aside>
</main>

<ng-template #loading>
  <main class="loading-state">{{ text('loading') }}</main>
</ng-template>



